import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Book as BookIcon,
  Keyboard as KeyboardIcon,
  Speed as SpeedIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const StudentTrackSheetsAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [trackSheets, setTrackSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    loadStudents();
  }, []);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_BASE_URL}/students?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setStudents(response.data.students || []);
      }
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const loadTrackSheets = async (studentId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_BASE_URL}/track-sheets/history?studentId=${studentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setTrackSheets(response.data.trackSheets || []);
      }
    } catch (error) {
      console.error('Error loading track sheets:', error);
      toast.error('Failed to load track sheets');
    } finally {
      setLoading(false);
    }
  };

  const loadSheetDetails = async (date, studentId) => {
    try {
      setLoadingDetails(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_BASE_URL}/track-sheets/${date}?studentId=${studentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.trackSheet) {
        setSelectedSheet(response.data.trackSheet);
        setDetailsOpen(true);
      }
    } catch (error) {
      console.error('Error loading sheet details:', error);
      toast.error('Failed to load track sheet details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleStudentChange = (event) => {
    const studentId = event.target.value;
    setSelectedStudent(studentId);
    if (studentId) {
      loadTrackSheets(studentId);
    } else {
      setTrackSheets([]);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStudentName = (studentId) => {
    const student = students.find((s) => s.id === studentId);
    return student ? student.name : 'Unknown';
  };

  if (loading && students.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Student Track Sheets - Admin View
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Student</InputLabel>
            <Select value={selectedStudent} onChange={handleStudentChange} label="Select Student">
              <MenuItem value="">
                <em>Select a student</em>
              </MenuItem>
              {students.map((student) => (
                <MenuItem key={student.id} value={student.id}>
                  {student.name} ({student.phone}) - {student.student_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {selectedStudent && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Track Sheets for {getStudentName(selectedStudent)}
            </Typography>
            {trackSheets.length === 0 ? (
              <Alert severity="info">No track sheets found for this student</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Lessons</TableCell>
                      <TableCell align="center">Shortcuts</TableCell>
                      <TableCell align="center">Speed Practice</TableCell>
                      <TableCell align="center">Exams</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trackSheets.map((sheet) => (
                      <TableRow key={sheet.id}>
                        <TableCell>{formatDate(sheet.track_date)}</TableCell>
                        <TableCell align="center">
                          <Chip label={sheet.lesson_count || 0} size="small" color="primary" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={sheet.shortcut_count || 0} size="small" color="secondary" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={sheet.speed_practice_count || 0} size="small" color="info" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={sheet.exam_count || 0} size="small" color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => loadSheetDetails(sheet.track_date, selectedStudent)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Track Sheet Details - {selectedSheet && formatDate(selectedSheet.track_date)}
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedSheet ? (
            <Box>
              {/* Lesson Details */}
              {selectedSheet.lessonDetails && selectedSheet.lessonDetails.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BookIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lesson Details
                    </Typography>
                  </Box>
                  {selectedSheet.lessonDetails.map((lesson, index) => (
                    <Box key={index} sx={{ ml: 4, mb: 2 }}>
                      <Typography variant="body1" fontWeight="medium" gutterBottom>
                        {lesson.lesson_name}
                      </Typography>
                      {lesson.points && lesson.points.length > 0 && (
                        <List dense>
                          {lesson.points.map((point, pIndex) => (
                            <ListItem key={pIndex}>
                              <ListItemText
                                primary={`• ${point.point_text}`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Shortcuts */}
              {selectedSheet.shortcuts && selectedSheet.shortcuts.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <KeyboardIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Shortcuts
                    </Typography>
                  </Box>
                  <List dense>
                    {selectedSheet.shortcuts.map((shortcut, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${shortcut.shortcut_key} - ${shortcut.use_case}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Speed Practice */}
              {selectedSheet.speedPractice && selectedSheet.speedPractice.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SpeedIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Speed Practice
                    </Typography>
                  </Box>
                  <List dense>
                    {selectedSheet.speedPractice.map((practice, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${practice.practice_type} - ${practice.lesson_name} - ${practice.speed}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Exam Results */}
              {selectedSheet.examResults && selectedSheet.examResults.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Exam Results
                    </Typography>
                  </Box>
                  <List dense>
                    {selectedSheet.examResults.map((exam, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${exam.exam_name}: ${exam.obtained_marks}/${exam.total_marks} (${exam.percentage.toFixed(2)}%)`}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {!selectedSheet.lessonDetails?.length &&
                !selectedSheet.shortcuts?.length &&
                !selectedSheet.speedPractice?.length &&
                !selectedSheet.examResults?.length && (
                  <Alert severity="info">No data recorded for this date</Alert>
                )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentTrackSheetsAdmin;





